import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Package, Plus, Trash2, Edit2, X, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CoachPackages = () => {
  const { getAuthHeaders } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    sessions_count: 1,
    price: 0,
    description: '',
    is_active: true
  });

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/session-packages`, { headers: getAuthHeaders() });
      setPackages(response.data);
    } catch (error) {
      toast.error('Failed to load packages');
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingPackage) {
        await axios.put(`${API}/session-packages/${editingPackage.id}`, formData, { headers: getAuthHeaders() });
        toast.success('Package updated');
      } else {
        await axios.post(`${API}/session-packages`, formData, { headers: getAuthHeaders() });
        toast.success('Package created');
      }
      closeForm();
      fetchPackages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save package');
    }
    setSubmitting(false);
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg);
    setFormData({
      name: pkg.name,
      sessions_count: pkg.sessions_count,
      price: pkg.price,
      description: pkg.description || '',
      is_active: pkg.is_active
    });
    setShowForm(true);
  };

  const handleDelete = async (packageId) => {
    if (!window.confirm('Delete this package?')) return;
    try {
      await axios.delete(`${API}/session-packages/${packageId}`, { headers: getAuthHeaders() });
      toast.success('Package deleted');
      fetchPackages();
    } catch (error) {
      toast.error('Failed to delete package');
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingPackage(null);
    setFormData({
      name: '',
      sessions_count: 1,
      price: 0,
      description: '',
      is_active: true
    });
  };

  // Common package suggestions
  const packageSuggestions = [
    { name: 'Single Session', sessions_count: 1, price: 75 },
    { name: '4-Session Pack', sessions_count: 4, price: 260 },
    { name: '8-Session Pack', sessions_count: 8, price: 480 },
    { name: '12-Session Pack', sessions_count: 12, price: 660 },
  ];

  const applySuggestion = (suggestion) => {
    setFormData({
      ...formData,
      name: suggestion.name,
      sessions_count: suggestion.sessions_count,
      price: suggestion.price
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="coach-packages">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl uppercase tracking-tight">Session Packages</h1>
          <p className="text-muted-foreground">Create custom packages for your athletes</p>
        </div>
        <Button className="bg-primary text-black hover:bg-primary/90" onClick={() => setShowForm(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Package
        </Button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border p-6 rounded-sm w-full max-w-lg">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl uppercase">
                {editingPackage ? 'Edit Package' : 'New Package'}
              </h2>
              <button onClick={closeForm} className="text-muted-foreground hover:text-foreground">
                <X size={20} />
              </button>
            </div>

            {/* Quick suggestions */}
            {!editingPackage && (
              <div className="mb-6">
                <p className="text-sm text-muted-foreground mb-2">Quick suggestions:</p>
                <div className="flex flex-wrap gap-2">
                  {packageSuggestions.map((suggestion, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => applySuggestion(suggestion)}
                      className="px-3 py-1 text-xs bg-muted hover:bg-muted/80 rounded-sm transition-colors"
                    >
                      {suggestion.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Package Name</label>
                <Input
                  placeholder="e.g., Single Session, 4-Session Pack"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Number of Sessions</label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.sessions_count}
                    onChange={(e) => setFormData({...formData, sessions_count: parseInt(e.target.value)})}
                    required
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Price ($)</label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value)})}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Description (optional)</label>
                <Textarea
                  placeholder="Package details..."
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                />
              </div>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="rounded"
                />
                <span className="text-sm">Active package (available for sale)</span>
              </label>

              {/* Preview */}
              {formData.sessions_count > 0 && formData.price > 0 && (
                <div className="bg-muted/30 p-3 rounded-sm">
                  <p className="text-sm text-muted-foreground">
                    Price per session: <span className="text-primary font-semibold">
                      ${(formData.price / formData.sessions_count).toFixed(2)}
                    </span>
                  </p>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={closeForm} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} className="flex-1 bg-primary text-black">
                  {submitting ? 'Saving...' : editingPackage ? 'Update' : 'Create Package'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Packages List */}
      {packages.length === 0 ? (
        <div className="bg-card border border-border/40 p-12 rounded-sm text-center">
          <Package className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-2">No packages created</p>
          <p className="text-sm text-muted-foreground mb-4">
            Create session packages to offer more options to your athletes
          </p>
          <Button onClick={() => setShowForm(true)}>Create First Package</Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className={`bg-card border rounded-sm p-5 ${
                pkg.is_active ? 'border-border/40' : 'border-border/20 opacity-60'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-lg">{pkg.name}</h3>
                  {!pkg.is_active && (
                    <span className="text-xs text-muted-foreground">(Inactive)</span>
                  )}
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleEdit(pkg)}
                    className="p-1.5 hover:bg-muted rounded-sm transition-colors"
                  >
                    <Edit2 size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(pkg.id)}
                    className="p-1.5 hover:bg-muted rounded-sm text-red-400 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              <div className="flex items-baseline gap-2 mb-3">
                <span className="text-3xl font-bold text-primary">
                  ${pkg.price.toFixed(0)}
                </span>
                <span className="text-muted-foreground text-sm">
                  / {pkg.sessions_count} {pkg.sessions_count === 1 ? 'session' : 'sessions'}
                </span>
              </div>

              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                <DollarSign size={14} />
                <span>${(pkg.price / pkg.sessions_count).toFixed(2)} per session</span>
              </div>

              {pkg.description && (
                <p className="text-sm text-muted-foreground mt-3 pt-3 border-t border-border/40">
                  {pkg.description}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CoachPackages;
